import React, { useEffect, useRef, useState } from 'react';
import ReceiptContent from './ReceiptContent';
import './Receipt.css'; // Import the print styles
import storeService from '../../services/storeService';
import { Capacitor } from '@capacitor/core';
import { getPreferredPrinterConfig } from '../../utils/printerPreferences';
import { formatInStoreTimezone } from '../../utils/dateUtils';

const currency = (value = 0) => `$${Number(value || 0).toFixed(2)}`;

const ReceiptModal = ({ sale, store: initialStore, onClose, tyroReceipts }) => {
  const receiptRef = useRef();
  const [storeDetails, setStoreDetails] = useState(initialStore || (typeof sale?.store === 'object' ? sale.store : null));
  const [printerError, setPrinterError] = useState('');
  const resolveTyroReceiptCopies = (receipts) => {
    if (Array.isArray(receipts?.copies) && receipts.copies.length > 0) {
      return receipts.copies.filter((copy) => copy?.customerReceipt || copy?.merchantReceipt);
    }
    if (receipts?.customerReceipt || receipts?.merchantReceipt) {
      return [
        {
          customerReceipt: receipts.customerReceipt || '',
          merchantReceipt: receipts.merchantReceipt || '',
          tyroTransactionId: receipts.tyroTransactionId || undefined,
        },
      ];
    }
    return [];
  };

  const buildPrintableHtml = () => {
    if (!receiptRef.current) {
      return null;
    }
    const receiptMarkup = receiptRef.current.outerHTML;
    const headContent = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('\n');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Receipt</title>
          ${headContent}
          <style>
            body {
              margin: 0;
              padding: 12px;
              background: #fff;
            }
            #receipt-to-print {
              width: 288px;
              margin: 0 auto;
            }
          </style>
        </head>
        <body>
          ${receiptMarkup}
        </body>
      </html>
    `;
  };

  const getPrinterPlugin = () => {
    if (!Capacitor.isPluginAvailable('ReceiptPrinter')) {
      return null;
    }
    return window?.Capacitor?.Plugins?.ReceiptPrinter || null;
  };

  const tryNativePrint = async (html) => {
    const plugin = getPrinterPlugin();
    if (!plugin) {
      setPrinterError(
        'Receipt printer not available. Please check whether it is powered on and connected to BoltinPOS.'
      );
      return false;
    }
    try {
      const paymentMethod = (sale?.paymentMethod || '').toString();
      const jobType =
        paymentMethod && paymentMethod.toLowerCase() === 'cash'
          ? 'cash'
          : 'receipt';
      console.debug('[ReceiptModal] Printing jobType', jobType, 'paymentMethod', paymentMethod);
      const payload = { html, jobType };
      const printerConfig = getPreferredPrinterConfig('receipt');
      if (printerConfig.transport === 'network' && printerConfig.host) {
        payload.networkHost = printerConfig.host;
        if (printerConfig.port) {
          payload.networkPort = printerConfig.port;
        }
      } else if (printerConfig.bluetoothName) {
        payload.bluetoothNameHint = printerConfig.bluetoothName;
      }
      await plugin.print(payload);
      return true;
    } catch (err) {
      console.error('[ReceiptModal] Native print failed', err);
      setPrinterError(
        'Unable to print via the connected receipt printer. Please ensure the printer is on and connected.'
      );
      return false;
    }
  };

  const handlePrint = async () => {
    const printableHtml = buildPrintableHtml();
    if (!printableHtml) {
      window.print();
      return;
    }

    if (await tryNativePrint(printableHtml)) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=420,height=800');
    if (!printWindow) {
      window.print();
      return;
    }

    printWindow.document.write(printableHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 200);
  };

  useEffect(() => {
    setStoreDetails(initialStore || (typeof sale?.store === 'object' ? sale.store : null));
  }, [initialStore, sale]);

  useEffect(() => {
    let isMounted = true;
    const loadStore = async () => {
      const storeId =
        initialStore?._id ||
        (typeof sale?.store === 'string' ? sale.store : sale?.store?._id);

      if ((!storeDetails || !storeDetails.receiptSettings) && storeId) {
        try {
          const data = await storeService.getStoreById(storeId);
          if (isMounted) {
            setStoreDetails(data);
          }
        } catch (err) {
          console.error('Failed to load store details for receipt', err);
        }
      }
    };

    loadStore();
    return () => { isMounted = false; };
  }, [initialStore, sale, storeDetails]);

const buildTyroPrintableMarkup = ({ title, sections }) => {
  const sectionMarkup = sections
    .filter(({ text }) => !!text)
    .map(
      ({ heading, text }) => `
        <div style="margin-bottom: 16px;">
          <div style="font-weight: 700; font-size: 12px; letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 6px;">
            ${heading}
          </div>
          <pre style="white-space: pre-wrap; font-size: 12px; line-height: 1.4; margin: 0;">${text}</pre>
        </div>
      `
    )
    .join('\n');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        <style>
          body {
            margin: 0;
            font-family: "Courier New", Courier, monospace;
            background: #fff;
            padding: 12px;
            width: 288px;
          }
          @page { margin: 8px; }
        </style>
      </head>
      <body>
        ${sectionMarkup}
      </body>
    </html>
  `;
};

const printTyroReceipt = async (receiptText, label) => {
  if (!receiptText) return;
  const printableMarkup = buildTyroPrintableMarkup({
    title: label,
    sections: [{ heading: label, text: receiptText }],
  });
  const plugin = getPrinterPlugin();

  if (plugin) {
    try {
      await plugin.print({ html: printableMarkup, jobType: 'receipt' });
      return;
    } catch (err) {
      console.error('[ReceiptModal] Tyro copy native print failed', err);
    }
  }

  const printWindow = window.open('', '_blank', 'width=420,height=800');
  if (!printWindow) {
    window.alert('Unable to open print preview. Please allow popups.');
    return;
  }
  printWindow.document.write(printableMarkup);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 200);
};

const printCombinedTyroReceipts = async (receipts) => {
  const receiptCopies = resolveTyroReceiptCopies(receipts);
  if (!receiptCopies.length) return;
  const printableMarkup = buildTyroPrintableMarkup({
    title: 'EFTPOS Receipts',
    sections: receiptCopies.flatMap((copy, index) => [
      {
        heading: `Split leg ${index + 1} - Customer copy${
          copy?.tyroTransactionId ? ` (${copy.tyroTransactionId})` : ''
        }`,
        text: copy.customerReceipt,
      },
      {
        heading: `Split leg ${index + 1} - Merchant copy${
          copy?.tyroTransactionId ? ` (${copy.tyroTransactionId})` : ''
        }`,
        text: copy.merchantReceipt,
      },
    ]),
  });
  const plugin = getPrinterPlugin();
  if (plugin) {
    try {
      await plugin.print({ html: printableMarkup, jobType: 'receipt' });
      return;
    } catch (err) {
      console.error('[ReceiptModal] Tyro combined print failed', err);
    }
  }

  const printWindow = window.open('', '_blank', 'width=420,height=800');
  if (!printWindow) {
    window.alert('Unable to open print preview. Please allow popups.');
    return;
  }
  printWindow.document.write(printableMarkup);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 200);
};

  if (!sale) return null;

  const saleTotal = Number(sale.finalAmount ?? sale.total ?? sale.amountDue ?? 0);
  const subtotal = Number(sale.subtotal ?? Math.max(saleTotal - (sale.gstAmount ?? 0), 0));
  const gstAmount = Number(sale.gstAmount ?? 0);
  const totalDiscount = Number(sale.totalDiscount ?? sale.discount ?? 0);
  const manualDiscountTotal = Array.isArray(sale.items)
    ? sale.items.reduce((sum, item) => sum + Number(item.manualDiscount || 0), 0)
    : 0;
  const rounding = Number(sale.rounding ?? 0);
  const loyaltyDiscount = Number(sale.loyaltyDiscount ?? sale.loyaltyRedemption?.discountAmount ?? 0);
  const tyroReceiptCopies = resolveTyroReceiptCopies(tyroReceipts);
  const timezone = storeDetails?.timezone || 'Australia/Melbourne';
  const createdAt = sale?.createdAt
    ? formatInStoreTimezone(sale.createdAt, 'dd-MM-yyyy h:mm a', timezone)
    : '';
  const itemCount = Array.isArray(sale.items)
    ? sale.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
    : 0;

  const renderTyroReceiptPreview = (label, receiptText) => (
    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs tracking-[0.3em] uppercase text-slate-500">{label}</p>
        <button
          onClick={() => printTyroReceipt(receiptText, `${label} Receipt`)}
          className="text-xs font-semibold text-primary hover:text-primary-dark"
        >
          Print
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto rounded-xl bg-white border border-slate-200 p-3">
        <pre className="text-[11px] whitespace-pre-wrap text-slate-700">{receiptText}</pre>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-5xl bg-white rounded-[32px] shadow-2xl h-full max-h-[92vh] flex flex-col overflow-hidden">
        <div className="grid grid-cols-1 flex-1 overflow-hidden xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
          <div className="bg-slate-50 flex flex-col h-full">
          <div className="flex items-start justify-between px-5 sm:px-10 py-6 border-b border-slate-200 gap-4">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-slate-500">Receipt preview</p>
              <h2 className="text-2xl font-semibold text-slate-900 mt-1">
                {sale?.receiptNumber ? `Receipt #${sale.receiptNumber}` : 'Sale complete'}
              </h2>
              {createdAt && <p className="text-sm text-slate-500 mt-1">{createdAt}</p>}
              <div className="flex flex-wrap gap-2 mt-4 text-xs uppercase tracking-wide">
                {itemCount > 0 && (
                  <span className="px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-700">
                    {itemCount} item{itemCount === 1 ? '' : 's'}
                  </span>
                )}
                <span className="px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-700">
                  {sale.paymentMethod || 'Payment'}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {printerError && (
                <div className="bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-xl px-3 py-2 max-w-xs">
                  {printerError}
                </div>
              )}
              <button
                onClick={handlePrint}
                className="px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors"
              >
                Print receipt
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full border border-slate-300 text-slate-600 text-sm font-semibold hover:border-slate-400 hover:text-slate-900 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 sm:px-10 py-4">
            <div className="bg-white rounded-[28px] shadow-inner p-4 sm:p-6 max-w-sm mx-auto max-h-[70vh] overflow-y-auto">
              <ReceiptContent
                ref={receiptRef}
                sale={sale}
                store={storeDetails}
                tyroReceiptCopies={tyroReceiptCopies}
              />
            </div>
          </div>
        </div>

          <div className="bg-white flex flex-col gap-6 p-6 sm:p-8 border-t border-slate-200 xl:border-t-0 xl:border-l overflow-y-auto">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-slate-500">Sale summary</p>
            <div className="mt-4 space-y-2 text-base text-slate-700">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{currency(subtotal)}</span>
              </div>
              {totalDiscount > 0 && (
                <div className="flex justify-between text-rose-600">
                  <span>Discounts</span>
                  <span>- {currency(totalDiscount)}</span>
                </div>
              )}
              {manualDiscountTotal > 0 && (
                <div className="flex justify-between text-rose-600">
                  <span>Manual discounts included</span>
                  <span>- {currency(manualDiscountTotal)}</span>
                </div>
              )}
              {loyaltyDiscount > 0 && (
                <div className="flex justify-between text-primary">
                  <span>Loyalty applied</span>
                  <span>- {currency(loyaltyDiscount)}</span>
                </div>
              )}
              {rounding !== 0 && (
                <div className="flex justify-between">
                  <span>Cash rounding</span>
                  <span>{rounding > 0 ? '+' : '-'}{currency(Math.abs(rounding))}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>GST</span>
                <span>{currency(gstAmount)}</span>
              </div>
            </div>
            <div className="mt-4 border-t border-slate-200 pt-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-slate-500">Total paid</p>
                  <p className="text-3xl font-semibold text-slate-900">{currency(saleTotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Payment method</p>
                  <p className="text-lg font-medium text-slate-900">
                    {Array.isArray(sale?.paymentAllocations) && sale.paymentAllocations.length > 1
                      ? 'Split'
                      : sale.paymentMethod || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {tyroReceiptCopies.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-500">EFTPOS receipt</p>
              <div className="mt-4 space-y-4">
                {tyroReceiptCopies.map((copy, index) => (
                  <div key={`${copy.tyroTransactionId || 'copy'}-${index}`} className="space-y-3">
                    <p className="text-[11px] tracking-[0.2em] uppercase text-slate-500">
                      Split leg {index + 1}
                      {copy.tyroTransactionId ? ` • ${copy.tyroTransactionId}` : ''}
                    </p>
                    {copy.customerReceipt &&
                      renderTyroReceiptPreview('Customer copy', copy.customerReceipt)}
                    {copy.merchantReceipt &&
                      renderTyroReceiptPreview('Merchant copy', copy.merchantReceipt)}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-auto pt-2 space-y-4">
            {tyroReceiptCopies.length > 0 && (
              <button
                onClick={() => printCombinedTyroReceipts(tyroReceipts)}
                className="w-full bg-secondary text-white py-3 rounded-2xl font-semibold hover:bg-secondary-dark transition-colors"
              >
                Print EFTPOS copies (single page)
              </button>
            )}

            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                onClick={onClose}
                className="bg-slate-100 text-slate-700 py-3 rounded-2xl font-semibold text-lg hover:bg-slate-200 transition-colors"
              >
                Skip receipt
              </button>
              <button
                onClick={onClose}
                className="bg-primary text-white py-3 rounded-2xl font-semibold text-lg hover:bg-primary-dark transition-colors"
              >
                Start new sale
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
